/** @module Queries for every scan of every user and for the testers' feedback. */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type components, problemFrom } from "@/api/client";

export type ScanDetail = components["schemas"]["ScanDetailOut"];
export type Feedback = components["schemas"]["FeedbackOut"];
export type Outcome =
  "open" | "pending" | "failed" | "auto" | "confirmed" | "corrected" | "dismissed" | "unapproved";
export const OUTCOMES: Outcome[] = [
  "open",
  "auto",
  "confirmed",
  "corrected",
  "dismissed",
  "unapproved",
  "failed",
  "pending",
];

export interface ScanFilters {
  userId: string | null;
  outcome: Outcome | null;
  excluded: boolean | null;
}

const PAGE = 48;

export function useAdminScans(filters: ScanFilters) {
  return useInfiniteQuery({
    queryKey: ["admin", "scan-list", filters],
    queryFn: async ({ pageParam }) => {
      const { data, response } = await api.GET("/api/v1/admin/scan-list", {
        params: {
          query: {
            limit: PAGE,
            user_id: filters.userId ?? undefined,
            outcome: filters.outcome ?? undefined,
            training_excluded: filters.excluded ?? undefined,
            cursor: pageParam ?? undefined,
          },
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/admin/users", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

/** Exclude or delete: both change the list, so both refresh it. */
export function useScanActions(scanId: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "scan-list"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "scans"] });
  };
  const exclude = useMutation({
    mutationFn: async (excluded: boolean) => {
      const { data, response } = await api.PATCH("/api/v1/admin/scans/{scan_id}", {
        params: { path: { scan_id: scanId } },
        body: { training_excluded: excluded },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: async () => {
      // The tester's card stays in their collection; only the photo goes.
      const { response } = await api.DELETE("/api/v1/scans/{scan_id}", {
        params: { path: { scan_id: scanId }, query: { keep_card: true } },
      });
      if (!response.ok && response.status !== 404) throw problemFrom(response);
    },
    onSuccess: refresh,
  });
  return { exclude, remove };
}

export function useFeedbackList(status: "open" | "done" | "all") {
  return useInfiniteQuery({
    queryKey: ["admin", "feedback", status],
    queryFn: async ({ pageParam }) => {
      const { data, response } = await api.GET("/api/v1/admin/feedback", {
        params: { query: { status, cursor: pageParam ?? undefined } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
    refetchInterval: 60_000,
  });
}

export function useMarkFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { data, response } = await api.PATCH("/api/v1/admin/feedback/{feedback_id}", {
        params: { path: { feedback_id: id } },
        body: { done },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
  });
}
